<template>
  <div class="p-6 bg-slate-50 min-h-screen">

    <!-- 1. Organization Header Card -->
    <div class="w-full mb-8">
  <div class="bg-white rounded-xl border border-gray-200 shadow-sm relative overflow-hidden">

    <!-- Setup Banner -->
    <div
      class="w-full px-6 py-3 flex items-center justify-between cursor-pointer transition-colors border-b"
      :class="isOnboardingComplete
        ? 'bg-green-50 border-green-100 hover:bg-green-100 text-green-800'
        : 'bg-blue-50 border-blue-100 hover:bg-blue-100 text-blue-800'"
      @click="goToOnboarding"
    >
      <div class="flex items-center gap-3">
        <div
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full"
          :class="isOnboardingComplete
            ? 'bg-green-200 text-green-700'
            : 'bg-blue-200 text-blue-700'"
        >
          <feather-icon
            :icon="isOnboardingComplete ? 'CheckIcon' : 'InfoIcon'"
            class="w-4 h-4"
          />
        </div>

        <div class="flex flex-col md:flex-row md:items-center gap-1 md:gap-2">
          <span class="font-bold">
            {{
              isOnboardingComplete
                ? $t('orgDashboard.organization.allSet')
                : $t('orgDashboard.organization.stepInProgress')
            }}
          </span>

          <span class="text-sm md:text-base opacity-90">
            {{
              isOnboardingComplete
                ? $t('orgDashboard.organization.completeProg')
                : $t('orgDashboard.organization.incompleteProg', {
                    onboardingStep
                  })
            }}
          </span>
        </div>
      </div>

      <div class="flex items-center gap-1 text-sm font-bold whitespace-nowrap pl-4">
        <span>
          {{
            isOnboardingComplete
              ? $t('orgDashboard.organization.viewDetails')
              : $t('orgDashboard.organization.continueSetup')
          }}
        </span>

        <feather-icon
          icon="ArrowRightIcon"
          class="w-4 h-4"
        />
      </div>
    </div>

    <!-- Card Content -->
    <div class="p-6 md:p-8 relative">
      <div
        v-if="orgData"
        class="flex flex-col lg:flex-row items-center lg:items-center gap-8 z-10 relative"
      >

        <!-- ====================================================== -->
        <!-- LEFT: ORGANIZATION INFO -->
        <!-- ====================================================== -->
        <div class="flex items-center gap-6 flex-1 min-w-0 w-full">

          <!-- Logo -->
          <div class="flex-shrink-0">
            <vs-avatar
              v-if="orgData.logo"
              class="shadow-md ring-4 ring-gray-50"
              size="140px"
              :src="constants.s3Bucket + '/organization/logo/' + orgData.logo"
            />

            <vs-avatar
              v-else
              class="shadow-md ring-4 ring-gray-50"
              size="140px"
              :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
            />
          </div>

          <!-- Organization Details -->
          <div class="flex flex-col items-start gap-2 min-w-0">
            <div class="flex items-center gap-2 text-gray-500 font-medium mb-1">
              <span>
                {{ $t('orgDashboard.organization.welcomeBack') }}
              </span>
            </div>

            <h1 class="text-4xl font-bold text-gray-800 tracking-tight">
              {{ orgData.name }}
            </h1>

            <div class="flex items-center gap-4 text-gray-500 mt-1 flex-wrap">

              <!-- Website -->
              <div class="flex items-center gap-2">
                <feather-icon
                  icon="GlobeIcon"
                  class="w-4 h-4"
                />

                <a
                  v-if="orgData.website"
                  :href="formattedWebsiteUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="hover:text-blue-600 transition-colors font-medium"
                >
                  {{ orgData.website }}
                </a>

                <span
                  v-else
                  class="text-gray-400 cursor-not-allowed"
                >
                  {{ $t('orgDashboard.organization.addWebsiteLink') }}
                </span>
              </div>

              <div
                v-if="orgData.website"
                class="h-4 w-px bg-gray-300"
              ></div>

              <!-- Copy -->
              <div
                v-if="orgData.website"
                class="cursor-pointer hover:text-gray-800 flex items-center gap-1 group"
                @click.stop="copyText"
              >
                <feather-icon
                  :icon="isTextCopied ? 'CheckIcon' : 'CopyIcon'"
                  class="w-4 h-4 group-hover:scale-110 transition-transform"
                />

                <span class="text-sm font-medium">
                  {{
                    isTextCopied
                      ? $t('orgDashboard.organization.linkCopied')
                      : $t('orgDashboard.organization.copyLink')
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- ====================================================== -->
<!-- RIGHT: EVALUATION FLOW -->
<!-- ====================================================== -->
<div class="w-full lg:flex-1 min-w-0">

  <!-- Header -->
  <div class="mb-4 px-1 flex items-center justify-between">
    <div class="text-left">
      <p class="text-xs font-bold uppercase tracking-widest text-gray-400">
        Evaluation Overview
      </p>

      <p class="text-xs text-gray-400 mt-1">
        Roles, available talent and evaluated talent
      </p>
    </div>

    <div
      v-if="isEvaluationLoading"
      class="flex items-center gap-2 text-xs text-gray-400"
    >
      <div class="w-3.5 h-3.5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
      Loading
    </div>
  </div>

  <!-- Evaluation Flow -->
  <div class="flex items-stretch gap-3 w-full">

    <!-- KEY ROLES -->
    <div
      class="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 px-5 py-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
      @click="goToOrgPlan"
    >
      <div class="flex items-start justify-between gap-4 h-full">

        <div class="text-left relative summary-tooltip-wrapper w-max">
          <span class="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
            Key Roles
          </span>

          <div class="mt-3 flex items-baseline gap-1">
            <span class="block text-4xl font-extrabold text-gray-800 leading-none">
              {{ metrics.kvdrSvrRoles }}
            </span>
            <span class="text-2xl font-bold text-gray-400">
              / {{ metrics.keyRoles }}
            </span>
          </div>

          <span class="block text-[11px] text-orange-500 font-semibold mt-4">
            Across Organization
          </span>
          
          <div class="plan-stat-tooltip" style="bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 10px;">
            {{ metrics.kvdrSvrRoles }} KVDR & SVR Roles / {{ metrics.keyRoles }} Total Activated Roles
          </div>
        </div>

        <div class="w-12 h-12 flex-shrink-0 rounded-xl bg-emerald-100 flex items-center justify-center">
          <svg
            class="w-10 h-10"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M9 19C9 18.2044 8.68393 17.4413 8.12132 16.8787C7.55871 16.3161 6.79565 16 6 16C5.20435 16 4.44129 16.3161 3.87868 16.8787C3.31607 17.4413 3 18.2044 3 19C3 19.7956 3.31607 20.5587 3.87868 21.1213C4.44129 21.6839 5.20435 22 6 22C6.79565 22 7.55871 21.6839 8.12132 21.1213C8.68393 20.5587 9 19.7956 9 19ZM9 5C9 4.20435 8.68393 3.44129 8.12132 2.87868C7.55871 2.31607 6.79565 2 6 2C5.20435 2 4.44129 2.31607 3.87868 2.87868C3.31607 3.44129 3 4.20435 3 5C3 5.79565 3.31607 6.55871 3.87868 7.12132C4.44129 7.68393 5.20435 8 6 8C6.79565 8 7.55871 7.68393 8.12132 7.12132C8.68393 6.55871 9 5.79565 9 5ZM6 8V16Z" fill="#FBB603" />
                <path d="M6 16C6.79565 16 7.55871 16.3161 8.12132 16.8787C8.68393 17.4413 9 18.2044 9 19C9 19.7956 8.68393 20.5587 8.12132 21.1213C7.55871 21.6839 6.79565 22 6 22C5.20435 22 4.44129 21.6839 3.87868 21.1213C3.31607 20.5587 3 19.7956 3 19C3 18.2044 3.31607 17.4413 3.87868 16.8787C4.44129 16.3161 5.20435 16 6 16ZM6 16V8M6 8C5.20435 8 4.44129 7.68393 3.87868 7.12132C3.31607 6.55871 3 5.79565 3 5C3 4.20435 3.31607 3.44129 3.87868 2.87868C4.44129 2.31607 5.20435 2 6 2C6.79565 2 7.55871 2.31607 8.12132 2.87868C8.68393 3.44129 9 4.20435 9 5C9 5.79565 8.68393 6.55871 8.12132 7.12132C7.55871 7.68393 6.79565 8 6 8Z" stroke="#737171" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M18 13.5853C18 12.6533 18 12.602 17.848 12.235C17.7475 11.9922 17.6001 11.7716 17.4143 11.5857C17.2284 11.3999 17.0078 11.2525 16.765 11.152C16.398 11 15.932 11 15 11H9C8.068 11 7.602 11 7.235 10.848C6.99218 10.7475 6.77155 10.6001 6.58572 10.4143C6.3999 10.2285 6.25251 10.0078 6.152 9.76501C6 9.39801 6 8.93201 6 8.00001" stroke="#737171" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M17.9998 16.5307V22M20.7344 19.2654H15.2651" stroke="#FBB603" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
        </div>

      </div>
    </div>


    <!-- PLUS -->
    <div class="flex items-center justify-center flex-shrink-0 px-1">
      <span class="text-3xl font-medium text-gray-400 leading-none">
        +
      </span>
    </div>


    <!-- TALENT POOL -->
    <div
      class="flex-1 min-w-0 bg-white rounded-xl border border-gray-200 px-5 py-4 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
      @click="goToTalentPool"
    >
      <div class="flex items-start justify-between gap-4 h-full">

        <div class="text-left relative summary-tooltip-wrapper w-max">
          <span class="block text-[11px] font-bold uppercase tracking-wider text-gray-400">
            Talent Pool
          </span>

          <div class="mt-3 flex items-baseline gap-1">
            <span class="block text-4xl font-extrabold text-gray-800 leading-none">
              {{ metrics.assignedTalent }}
            </span>
            <span class="text-2xl font-bold text-gray-400">
              / {{ metrics.talentPool }}
            </span>
          </div>

          <span class="block text-[11px] text-purple-500 font-semibold mt-4">
            View Talent 
          </span>

          <div class="plan-stat-tooltip" style="bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 10px;">
            {{ metrics.assignedTalent }} Talents assigned to roles / {{ metrics.talentPool }} Total talent in ecosystem
          </div>
        </div>

        
        <div class="w-12 h-12 flex-shrink-0 rounded-xl bg-emerald-100 flex items-center justify-center">
          <svg
            class="w-10 h-10"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" stroke="#737171" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <!-- Head filled with yellow, with grey outline -->
                  <circle cx="8.5" cy="7" r="4" fill="#FBB603" stroke="#737171" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></circle>
                  <!-- Plus sign changed to yellow -->
                  <line x1="20" y1="8" x2="20" y2="14" stroke="#FBB603" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></line>
                  <line x1="23" y1="11" x2="17" y2="11" stroke="#FBB603" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></line>
              </svg>
        </div>


      </div>
    </div>


    <!-- EQUALS -->
    <div class="flex items-center justify-center flex-shrink-0 px-1">
      <span class="text-3xl font-medium text-gray-400 leading-none">
        =
      </span>
    </div>


    <!-- EVALUATED -->
    <div
      class="flex-1 min-w-0 relative rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-white shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
    >

      <!-- Background glow wrapped so tooltips can escape -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none rounded-xl">
        <div
          class="absolute -top-10 -right-10 w-28 h-28 bg-emerald-100 rounded-full opacity-60 pointer-events-none"
        ></div>
      </div>

      <div class="relative px-5 py-4 flex items-start justify-between gap-4 h-full">

        <div class="text-left relative summary-tooltip-wrapper w-max">

          <div class="flex items-center gap-2">
            <span class="text-[11px] font-bold uppercase tracking-wider text-emerald-600">
              Evaluated
            </span>
          </div>

          <div
            v-if="!isEvaluationLoading"
            class="mt-3 flex items-baseline gap-1"
          >
            <span class="block text-4xl font-extrabold text-gray-800 leading-none">
              {{ metrics.evaluated }}
            </span>
            <span class="text-2xl font-bold text-gray-400">
              / {{ metrics.assignedTalent }}
            </span>
          </div>

          <div
            v-else
            class="w-12 h-9 rounded bg-gray-200 animate-pulse mt-3"
          ></div>

          <span class="block text-[11px] text-green-500 font-semibold mt-4">
            Talent Evaluated 
          </span>

          <div class="plan-stat-tooltip" style="bottom: 100%; left: 50%; transform: translateX(-50%); margin-bottom: 10px;">
            {{ metrics.evaluated }} Talents with fitment data / {{ metrics.assignedTalent }} Total assigned talents
          </div>

        </div>

      
        <div class="w-12 h-12 flex-shrink-0 rounded-xl bg-emerald-100 flex items-center justify-center">
          <svg
            class="w-10 h-10"
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M14 19.5H11C8.172 19.5 6.757 19.5 5.879 18.621C5 17.743 5 16.328 5 13.5V7.5M14 19.5C14 20.2 12.006 21.509 11.5 22M14 19.5C14 18.8 12.006 17.491 11.5 17" stroke="#F8BE17" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M8 5C8 4.20435 7.68393 3.44129 7.12132 2.87868C6.55871 2.31607 5.79565 2 5 2C4.20435 2 3.44129 2.31607 2.87868 2.87868C2.31607 3.44129 2 4.20435 2 5C2 5.79565 2.31607 6.55871 2.87868 7.12132C3.44129 7.68393 4.20435 8 5 8C5.79565 8 6.55871 7.68393 7.12132 7.12132C7.68393 6.55871 8 5.79565 8 5Z" fill="#FBB603" stroke="#737171" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M19 17V11C19 8.172 19 6.757 18.121 5.879C17.243 5 15.828 5 13 5H10M10 5C10 4.3 11.994 2.992 12.5 2.5M10 5C10 5.7 11.994 7.008 12.5 7.5" stroke="#737171" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M22 19C22 18.2044 21.6839 17.4413 21.1213 16.8787C20.5587 16.3161 19.7956 16 19 16C18.2044 16 17.4413 16.3161 16.8787 16.8787C16.3161 17.4413 16 18.2044 16 19C16 19.7956 16.3161 20.5587 16.8787 21.1213C17.4413 21.6839 18.2044 22 19 22C19.7956 22 20.5587 21.6839 21.1213 21.1213C21.6839 20.5587 22 19.7956 22 19Z" fill="#FBB603" stroke="#737171" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>

        </div>

      </div>
    </div>

  </div>
</div>
      </div>

      <!-- Decorative background -->
      <div
        class="absolute top-0 right-0 -mt-4 -mr-4 w-64 h-64 bg-gradient-to-bl from-blue-50 to-transparent rounded-full opacity-50 pointer-events-none"
      ></div>
    </div>
  </div>
</div>

    <!-- 3. Insights Section -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

      <!-- Organization Information -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
        <div class="p-6 flex-1">
          <h3 class="text-lg font-bold text-gray-800 mb-6 flex items-center gap-2">
            <feather-icon icon="InfoIcon" class="w-5 h-5 text-blue-500" />
            Organization Information
          </h3>

          <div>
            <p class="text-large font-semibold text-gray-400 uppercase tracking-wider mb-2">About Organization</p>
            <p v-if="organizationDetails.about" class="text-medium text-gray-600 leading-6 text-left">
              {{ organizationDetails.about }}
            </p>
            <p v-else class="text-medium text-gray-400 italic text-left">
              No organization description available.
            </p>
          </div>
        </div>

        <!-- Bottom Footer -->
        <div class="border-t border-gray-100 bg-gray-50/60 px-6 py-4">
          <div class="grid grid-cols-2 gap-8">
            <div class="flex items-center gap-3 justify-start">
              <div class="w-9 h-9 rounded-lg bg-purple-50 text-purple-500 flex items-center justify-center flex-shrink-0">
                <feather-icon icon="BriefcaseIcon" class="w-4 h-4" />
              </div>
              <div class="text-left">
                <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Organization Type</p>
                <span v-if="organizationDetails.organizationType" class="text-sm font-medium text-gray-700">
                  {{ organizationDetails.organizationType }}
                </span>
                <span v-else class="text-sm text-gray-400 italic">Not provided</span>
              </div>
            </div>

            <div class="flex items-center gap-3 justify-start">
              <div class="w-9 h-9 rounded-lg bg-blue-50 text-blue-500 flex items-center justify-center flex-shrink-0">
                <feather-icon icon="MapPinIcon" class="w-4 h-4" />
              </div>
              <div class="text-left">
                <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Location</p>
                <span v-if="organizationDetails.location" class="text-sm font-medium text-gray-700">
                  {{ organizationDetails.location }}
                </span>
                <span v-else class="text-sm text-gray-400 italic">Not provided</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- User Plans Velocity & Progress Insights -->
      <div class="bg-white rounded-xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between">
        
        <!-- Active User Plans Header -->
        <div>
          <h3 class="text-lg font-bold text-gray-800 mb-1 flex items-center gap-2">
            <feather-icon icon="ActivityIcon" class="w-5 h-5 text-gray-400" />
            Ongoing Plans
          </h3>
          <p class="text-gray-500 text-sm mb-6">
            Distribution of plans across organizational workspaces.
          </p>

          <!-- Summary Row -->
          <div class="flex items-center justify-between mb-5">
            <!-- Active (Participants in Progress) -->
            <div class="flex items-center gap-4">
              <div class="flex items-center justify-center">
                <feather-icon icon="LayersIcon" class="w-7 h-7 text-blue-400" />
              </div>
              <div>
                <!-- Added wrapper and tooltip here -->
                <div class="flex items-baseline gap-2 relative summary-tooltip-wrapper cursor-help">
                  <span class="text-3xl font-bold text-gray-800">
                    {{ metrics.activePlans }}
                  </span>
                  <span class="text-xs uppercase tracking-wide text-gray-500 font-bold">Active Plans</span>
                  
                  <div class="plan-stat-tooltip">
                    Plans currently at starting or in progress
                  </div>
                </div>
              </div>
            </div>

            <!-- Completed -->
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center">
                <feather-icon icon="CheckCircleIcon" class="w-7 h-7 text-green-400" />
              </div>
              <div class="text-right">
                <!-- Added wrapper and tooltip here -->
                <div class="flex items-baseline justify-end gap-2 relative summary-tooltip-wrapper cursor-help">
                  <span class="text-2xl font-bold text-gray-500">
                    {{ metrics.completedPlans }}
                  </span>
                  <span class="text-xs uppercase tracking-wide text-gray-400 font-bold">Completed Plans</span>
                  
                  <div class="plan-stat-tooltip">
                    Plans currently in final stage
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Active Plan Types Categories -->
          <div class="mt-3">
            <div class="relative">
              <!-- Colored Bar -->
              <div class="w-full bg-gray-100 rounded-full h-3 flex">
                <div
                  v-for="cat in planCategories"
                  :key="cat.name"
                  class="progress-segment relative h-full transition-all duration-700 first:rounded-l-full last:rounded-r-full"
                  :class="cat.color"
                  :style="{ width: cat.percent + '%' }"
                >
                  <!-- Clearer Custom Tooltip -->
                  <div class="plan-stat-tooltip">
                    {{ cat.count }} {{ cat.count === 1 ? 'plan' : 'plans' }} in {{ cat.name }}
                  </div>
                </div>
              </div>

              <!-- Labels -->
              <div class="relative w-full h-7 mt-2">
                <div
                  v-for="(cat, index) in planCategories"
                  :key="cat.name"
                  class="absolute flex items-center gap-1.5 whitespace-nowrap"
                  :style="{
                    left: getCategoryLabelPosition(index) + '%',
                    transform: 'translateX(-50%)'
                  }"
                >
                  <span class="w-2 h-2 rounded-full flex-shrink-0" :class="cat.color"></span>
                  <span class="text-xs text-gray-600 font-medium">{{ cat.name }}:</span>
                  <span class="text-xs font-bold text-gray-800">{{ cat.count }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ⭐️ REDESIGNED: Stages Progress Breakdown (Top 3 Plans) ⭐️ -->
        <div class="mt-6 pt-5 border-t border-gray-100">
          <div class="flex justify-between items-center mb-4">
            <span class="text-medium font-bold text-gray-800">
              Top 3 Plans Progress
            </span>
            <span
              class="text-xs text-blue-600 font-semibold cursor-pointer hover:underline flex items-center gap-1"
              @click="goToBoards"
            >
              View Board details
              <feather-icon icon="ArrowRightIcon" class="w-3 h-3" />
            </span>
          </div>

          <div v-if="isLoadingPlans" class="flex justify-center py-6">
            <feather-icon icon="RefreshCwIcon" class="w-6 h-6 text-blue-500 animate-spin" />
          </div>

          <div v-else-if="topPlans.length === 0" class="text-center text-sm text-gray-400 py-6">
            No active plans found.
          </div>

          <div v-else class="space-y-1">
            <div
              v-for="plan in topPlans"
              :key="plan.id"
              class="group flex items-center w-full py-2.5 px-2 rounded-lg
                     hover:bg-gray-50 transition-all cursor-pointer"
              @click="goToPlanBoard(plan)"
            >
              <div class="flex-1 min-w-0 text-left">
                <span
                  class="block text-md font-bold text-gray-700 text-left
                         group-hover:text-blue-600 transition-colors"
                >
                  {{ plan.title }}
                </span>
              </div>

              <!-- Status Numbers -->
              <div class="flex items-center flex-shrink-0">
                <!-- TO DO -->
                <div class="plan-stat w-24 text-center">
                  <span class="plan-stat-number todo">{{ plan.todo }}</span>
                  <div class="plan-stat-tooltip">
                    {{ plan.todo }} {{ plan.todo === 1 ? 'user is' : 'users are' }} starting the plan
                  </div>
                </div>

                <!-- DOING -->
                <div class="plan-stat w-24 text-center">
                  <span class="plan-stat-number doing">{{ plan.doing }}</span>
                  <div class="plan-stat-tooltip">
                    {{ plan.doing }} {{ plan.doing === 1 ? 'user is' : 'users are' }} in progress
                  </div>
                </div>

                <!-- DONE -->
                <div class="plan-stat w-24 text-center">
                  <span class="plan-stat-number done">{{ plan.done }}</span>
                  <div class="plan-stat-tooltip">
                    {{ plan.done }} {{ plan.done === 1 ? 'user has' : 'users have' }} completed the plan
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- 4. Quick Links Footer -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <div class="bg-white border border-gray-200 rounded-xl p-6 text-gray-800 shadow-sm cursor-pointer transform transition hover:-translate-y-1 hover:shadow-md" @click="goToReports">
          <div class="flex items-center justify-between mb-4">
             <div class="bg-blue-50 p-2 rounded-lg text-blue-600 flex items-center justify-center h-10 w-10">
                <feather-icon icon="PieChartIcon" class="w-6 h-6" />
             </div>
             <feather-icon icon="ArrowRightIcon" class="w-5 h-5 text-gray-400" />
          </div>
          <h4 class="text-xl font-bold mb-1">{{ $t('orgDashboard.organization.kvdrReports') }}</h4>
          <p class="text-gray-500 text-sm">{{ $t('orgDashboard.organization.kvdrReports_desc') }}</p>
        </div>

        <div class="bg-white border border-gray-200 rounded-xl p-6 text-gray-800 shadow-sm cursor-pointer transform transition hover:-translate-y-1 hover:shadow-md" @click="goToTalentPool">
           <div class="flex items-center justify-between mb-4">
             <div class="bg-purple-50 p-2 rounded-lg text-purple-600 flex items-center justify-center h-10 w-10">
                <feather-icon icon="UsersIcon" class="w-6 h-6" />
             </div>
             <feather-icon icon="ArrowRightIcon" class="w-5 h-5 text-gray-400" />
          </div>
          <h4 class="text-xl font-bold mb-1">{{ $t('orgDashboard.organization.talentExplorer') }}</h4>
          <p class="text-gray-500 text-sm">{{ $t('orgDashboard.organization.talentExplorer_desc') }}</p>
        </div>
    </div>

  </div>
</template>

<script>
import constants from "@/constants.js";
import hierarchyIcon from '@/assets/images/aboutUs/hierarchy-center.svg'
import talentPoolIcon from '@/assets/images/aboutUs/talent-pool.svg'
import evaluationIcon from '@/assets/images/aboutUs/evaluation.svg'

export default {
  data() {
    return {
      hierarchyIcon,
      talentPoolIcon,
      evaluationIcon,
      constants,
      onboardingStep: 1,
      isTextCopied: false,
      topPlans: [], 
      isEvaluationLoading: false,
      metrics: {
        talentPool: 0,
        assignedTalent: 0,
        keyRoles: 0,
        kvdrSvrRoles: 0,
        evaluated: 0,
        
        activePlans: 0,       
        completedPlans: 0,    
        todo: 0,              
        doing: 0,             
        done: 0,              
        
        categoryData: {
          ecosystem: 0,
          fitment: 0,
          development: 0,
          succession: 0
        }
      },
      topRoles: [],
      isLoadingPlans: false,
      organizationDetails: {
        about: '',
        organizationType: '',
        location: ''
      },
    };
  },
  computed: {
    orgData() {
      return this.$store.state.organization.orgData;
    },
    formattedWebsiteUrl() {
      const url = this.orgData?.website;
      if (!url) return '#';
      if (url.match(/^https?:\/\//)) return url;
      return `https://${url}`;
    },
    isOnboardingComplete() {
      return this.onboardingStep > 4;
    },
    planCategories() {
      const { ecosystem, fitment, development, succession } = this.metrics.categoryData;
      const total = ecosystem + fitment + development + succession || 1; 
      
      const categories = [
        { name: 'Ecosystem Building', count: ecosystem, percent: (ecosystem/total)*100, color: 'bg-purple-300' },
        { name: 'Fitment Plan', count: fitment, percent: (fitment/total)*100, color: 'bg-yellow-500' },
        { name: 'Development Plan', count: development, percent: (development/total)*100, color: 'bg-pink-500' },
        { name: 'Succession Plan', count: succession, percent: (succession/total)*100, color: 'bg-orange-500' }
      ];
      
      return categories.filter(c => c.count > 0);
    }
  },
  watch: {
    orgData: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal && newVal._id) {
          this.initDashboardData(newVal._id);
        }

        if (newVal && newVal.slug) {
          this.loadOnboardingStep();
        }
      }
    },
    '$store.state.organization.employees': {
      handler() {
        this.calculateTalentPoolMetrics();
      },
      deep: true
    }
  },
  methods: {
    getCategoryLabelPosition(index) {
      let position = 0;
      for (let i = 0; i < index; i++) {
        position += this.planCategories[i].percent;
      }
      return position + (this.planCategories[index].percent / 2);
    },
    
    initDashboardData(orgId) {
      this.fetchAllWorkspaceData(orgId);
      this.$store.dispatch("organization/fetchAllEmployees", { organizationId: orgId });
      this.fetchOrganizationDetails(orgId);
      this.fetchKvdrData(orgId); // Replaces separate role & eval fetches
    },

    // --- CONSOLIDATED FETCH: Pulls KVDRs & Roles to calculate Roles, Assignments & Fitment ---
    async fetchKvdrData(orgId) {
      this.isEvaluationLoading = true;
      try {
        const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken') || localStorage.getItem('token');
        
        let allRolesCount = 0;
        let kvdrSvrCount = 0;
        let assignedUserIds = new Set();
        let evaluatedUserIds = new Set();

        const res1 = await fetch(`https://api.getboarded.tech/kvdr?orgId=${orgId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (res1.ok) {
          const data1 = await res1.json();
          let content = data1.data || data1;
          
          if (!Array.isArray(content) && content && content._id) {
             content = [content];
          }

          if (Array.isArray(content)) {
            await Promise.all(content.map(async (kvdr) => {
              if (kvdr && kvdr._id) {
                try {
                  const rolesRes = await fetch(`https://api.getboarded.tech/kvdr-role?kvdrId=${kvdr._id}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                  });
                  
                  if (rolesRes.ok) {
                    const rolesData = await rolesRes.json();
                    const rolesList = rolesData.data || rolesData;
                    
                    if (Array.isArray(rolesList)) {
                      allRolesCount += rolesList.length;
                      
                      rolesList.forEach(r => {
                        // Tally Role Categories (KVDR / SVR / NVR)
                        const tag = (r.kvdrTag || '').toUpperCase();
                        if (tag === 'KVDR' || tag === 'SVR' || tag === 'NVR') {
                           kvdrSvrCount++;
                        }

                        // Inspect Profiles for Assignment & Fitment
                        const profiles = Array.isArray(r.kvdrRoleProfiles) ? r.kvdrRoleProfiles : [];
                        profiles.forEach(profile => {
                          const uId = profile.userId || profile._id;
                          if (uId) {
                            assignedUserIds.add(uId);

                            // Check fitment data presence
                            if (Array.isArray(profile.fitmentData) && profile.fitmentData.length > 0) {
                              evaluatedUserIds.add(uId);
                            }
                          }
                        });
                      });
                    }
                  }
                } catch (e) {
                  console.error("Failed fetching kvdr-role dynamically", e);
                }
              }
            }));
          }
        }
        
        // Update all metrics together
        this.metrics.keyRoles = allRolesCount;
        this.metrics.kvdrSvrRoles = kvdrSvrCount;
        this.metrics.assignedTalent = assignedUserIds.size;
        this.metrics.evaluated = evaluatedUserIds.size;

      } catch (err) {
        console.error("Failed to fetch KVDR data:", err);
      } finally {
        this.isEvaluationLoading = false;
      }
    },

    calculateTalentPoolMetrics() {
      // Just track the total number of talents across ecosystem.
      // The "Assigned" count is natively derived from the `fetchKvdrData` profile check above.
      const allEmployees = this.$store.state.organization.employees || [];
      this.metrics.talentPool = allEmployees.length;
    },

    goToPlanBoard(plan) {
      if (this.orgData && this.orgData.slug && plan && plan.id) {
        if (plan.isMongo) {
          this.$router.push(`/organization/${this.orgData.slug}/boards/${plan.id}`);
        } else {
          // Fallback if it's a Python/Coach plan without a Kanban board yet
          this.$router.push(`/organization/${this.orgData.slug}/boards`);
        }
      }
    },

    // Resolves Duplicate Kanban Entries via Meta API
    async fetchAllWorkspaceData(orgId) {
      this.isLoadingPlans = true;
      try {
        const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken') || localStorage.getItem('token');
        const pyApiUrl = process.env.VUE_APP_API_URL || "https://parser.ai.getboarded.com";
        const gatewayApiUrl = "https://api-core-gateway.getboarded.com";
        
        let allBoards = [];
        let coachPlans = [];
        let talentPlansMap = {};
        
        // 1. Fetch Mongo Boards
        try {
          const mongoRes = await fetch(`https://api.getboarded.tech/boards?organizationId=${orgId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const mongoData = await mongoRes.json();
          allBoards = Array.isArray(mongoData.data) ? mongoData.data : (mongoData.data?.data || []);
        } catch(e) { console.error("Failed fetching mongo boards", e); }

        // 2. Fetch Coach Plans (Python)
        try {
          const pyRes = await fetch(`${pyApiUrl}/api/v2/coach-plans/${orgId}`);
          const pyData = await pyRes.json();
          if (pyData.data) coachPlans = pyData.data;
        } catch(e) { console.error("Failed fetching coach plans", e); }

        // 3. Fetch Talent Plans Metadata
        try {
          const tRes = await fetch(`${pyApiUrl}/api/v2/talent-plans/org/${orgId}`);
          const tData = await tRes.json();
          if (tData.data) {
            tData.data.forEach(p => { talentPlansMap[p.board_id] = p; });
          }
        } catch(e) { console.error("Failed fetching talent metadata", e); }

        const normalize = str => (str || '').replace(/\s+/g, ' ').trim().toLowerCase();
        
        // --- MAP UNIFICATION ---
        const unifiedPlansMap = {};

        // Register Mongo Boards
        allBoards.forEach(board => {
            const normTitle = normalize(board.title);
            unifiedPlansMap[normTitle] = {
                id: board._id,
                mongoId: board._id,
                title: board.title,
                isMongo: true,
                assignedCount: 0 
            };
        });

        // Overlay/Merge Coach Plans
        coachPlans.forEach(plan => {
            const title = plan.title || plan.plan_name || 'Coach Plan';
            const normTitle = normalize(title);
            const userCount = Array.isArray(plan.assigned_users) ? plan.assigned_users.length : 0;
            
            if (unifiedPlansMap[normTitle]) {
                unifiedPlansMap[normTitle].isCoach = true;
                unifiedPlansMap[normTitle].assignedCount = userCount;
            } else {
                unifiedPlansMap[normTitle] = {
                    id: plan.id || plan._id || Math.random().toString(),
                    title: title,
                    isMongo: false,
                    isCoach: true,
                    assignedCount: userCount
                };
            }
        });

        let totalEcosystem = 0, totalFitment = 0, totalDevelopment = 0, totalSuccession = 0;
        let totalTodo = 0, totalDoing = 0, totalDone = 0;
        let plansList = [];

        // Iterate through all unified plans
        await Promise.all(Object.values(unifiedPlansMap).map(async (planObj) => {
            let boardTotalUsers = 0;
            let boardTodo = 0, boardDoing = 0, boardDone = 0;

            if (planObj.isMongo) {
                // 1. Fetch exactly how many total users exist using META API
                try {
                    const metaRes = await fetch(`${gatewayApiUrl}/boards/board-users/meta/${planObj.mongoId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const metaData = await metaRes.json();
                    if (metaData.data && Array.isArray(metaData.data)) {
                        boardTotalUsers = metaData.data.length;
                    }
                } catch(e) { console.warn("Meta API fail", e); }

                // 2. Fetch exactly where they are using KANBAN API
                try {
                    let kanbanUserStages = {}; 
                    const kanbanRes = await fetch(`${gatewayApiUrl}/boards/kanban/${planObj.mongoId}`, {
                        headers: { 'Authorization': `Bearer ${token}` }
                    });
                    const kanbanData = await kanbanRes.json();

                    if (kanbanData.data && Array.isArray(kanbanData.data.cols) && kanbanData.data.cols.length > 0) {
                        const cols = kanbanData.data.cols;
                        const numCols = cols.length;

                        cols.forEach((col, idx) => {
                            if (Array.isArray(col.colUsersData)) {
                                col.colUsersData.forEach(u => {
                                    const uid = u.userId || u._id;
                                    if (uid) kanbanUserStages[uid] = idx; 
                                });
                            }
                        });

                        Object.values(kanbanUserStages).forEach(idx => {
                            if (numCols === 1) boardDoing++;
                            else if (idx === numCols - 1) boardDone++; 
                            else if (idx > 0) boardDoing++;            
                        });
                    }
                } catch(e) { console.warn("Kanban API fail", e); }
                
                // 3. Mathematical fix for To Do
                boardTodo = boardTotalUsers - (boardDoing + boardDone);
                
                if (boardTodo < 0) {
                    boardTotalUsers = boardDoing + boardDone;
                    boardTodo = 0;
                }
            } else {
                boardTotalUsers = planObj.assignedCount;
                boardDoing = planObj.assignedCount; 
            }

            // Global Trackers Addition
            totalTodo += boardTodo;
            totalDoing += boardDoing;
            totalDone += boardDone;

            // Categorize for Bar Graph
            const titleLower = normalize(planObj.title);
            if (titleLower.includes('ecosystem') || titleLower.includes('hiring') || titleLower.includes('onboard')) totalEcosystem += boardTotalUsers;
            else if (titleLower.includes('fitment') || titleLower.includes('empanelment')) totalFitment += boardTotalUsers;
            else if (titleLower.includes('develop') || titleLower.includes('learn') || titleLower.includes('training')) totalDevelopment += boardTotalUsers;
            else totalSuccession += boardTotalUsers;

            plansList.push({
                id: planObj.id,
                title: planObj.title,
                todo: boardTodo,
                doing: boardDoing,
                done: boardDone,
                total: boardTotalUsers,
                isMongo: planObj.isMongo
            });

        }));

        plansList.sort((a, b) => b.total - a.total);
        this.topPlans = plansList.slice(0, 3);

        this.metrics.todo = totalTodo;
        this.metrics.doing = totalDoing; 
        this.metrics.done = totalDone;
        this.metrics.activePlans = totalTodo + totalDoing;
        this.metrics.completedPlans = totalDone;

        this.metrics.categoryData = {
            ecosystem: totalEcosystem,
            fitment: totalFitment,
            development: totalDevelopment,
            succession: totalSuccession
        };

      } catch(err) {
        console.error("Error computing workspace calculations:", err);
      } finally {
        this.isLoadingPlans = false;
      }
    },

    async fetchOrganizationDetails(orgId) {
      try {
        const token = localStorage.getItem('feathers-jwt') || localStorage.getItem('accessToken');
        const response = await fetch(`https://api.getboarded.tech/organization/${orgId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) throw new Error(`Failed to fetch org details: ${response.status}`);

        const result = await response.json();
        const data = result.data || result.organization || result;

        this.organizationDetails = {
          about: data.about || '',
          organizationType: data.organizationType || '',
          location: data.location || ''
        };
      } catch (error) {
        console.error('Failed to fetch organization details:', error);
      }
    },

    goToOnboarding() {
      if (this.orgData && this.orgData.slug) this.$router.push(`/organization/${this.orgData.slug}/onboarding`);
    },
    goToTalentPool() {
      if (this.orgData && this.orgData.slug) this.$router.push(`/organization/${this.orgData.slug}/talent`);
    },
    goToReports() {
      if (this.orgData && this.orgData.slug) this.$router.push(`/organization/${this.orgData.slug}/reports`);
    },
    goToBoards() {
      if (this.orgData && this.orgData.slug) this.$router.push(`/organization/${this.orgData.slug}/boards`);
    },
    goToOrgPlan() {
      if (this.orgData && this.orgData.slug) this.$router.push(`/organization/${this.orgData.slug}/roles`);
    },
    async copyText() {
      try {
        await navigator.clipboard.writeText(this.orgData.website);
        this.isTextCopied = true;
        setTimeout(() => (this.isTextCopied = false), 2000);
      } catch (err) {
        console.error("Failed to copy text: ", err);
      }
    },
    loadOnboardingStep() {
      if (this.orgData && this.orgData.slug) {
        const key = `onboardingStep_${this.orgData.slug}`;
        const savedStep = localStorage.getItem(key);
        this.onboardingStep = savedStep ? parseInt(savedStep) : 1;
      }
    }
  }
};
</script>

<style scoped>
.hover\:shadow-md:hover {
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.plan-stat {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 28px;
}

.plan-stat-number {
  font-size: 14px;
  font-weight: 900;
  cursor: help;
}

.plan-stat-number.todo {
  color: #ef4444;
}

.plan-stat-number.doing {
  color: #3b82f6;
}

.plan-stat-number.done {
  color: #22c55e;
}

.plan-stat-tooltip {
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);

  background: #1f2937;
  color: white;

  padding: 6px 10px;
  border-radius: 6px;

  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;

  white-space: nowrap;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;

  transition:
    opacity 0.15s ease,
    visibility 0.15s ease;

  z-index: 9999;
}

.plan-stat-tooltip::after {
  content: "";

  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);

  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 5px solid #1f2937;
}

.plan-stat:hover .plan-stat-tooltip,
.progress-segment:hover .plan-stat-tooltip,
.summary-tooltip-wrapper:hover .plan-stat-tooltip {
  opacity: 1;
  visibility: visible;
}
</style>